// ============================================================================
// CART DRAWER - Multi-Ship & Age Verification
// ============================================================================

// Configuration
const CONFIG = {
  CART_UPDATE_DELAY: 500,
  AGE_BOX_RETRY_DELAY: 100,
  LEGAL_DRINKING_AGE: 21,
  WINE_TAG: '& Wine',
  HIDE_MULTISHIP_TAG: 'Hide multiship'
};

// State management
let drawerFetchObserverSetup = false;
let drawerButtonHandlerSetup = false;
let drawerAgeVerificationSetup = false;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Detect wine products in cart using DOM tags and fallback keyword search
 */
function detectWineProducts(cart) {
  let hasWine = false;

  cart.items.forEach(item => {
    // Primary: Check DOM for accurate tag data
    const selector = `.cart-summary-item-container[data-title="${item.product_title.replace(/"/g, '&quot;')}"]`;
    const el = document.querySelector(selector);

    if (el) {
      const tags = el.dataset.tags ? el.dataset.tags.split(',') : [];
      if (tags.includes(CONFIG.WINE_TAG)) {
        hasWine = true;
        return;
      }
    }

    // Fallback: Check product data for wine keywords (handles DOM not ready)
    const productTitle = (item.product_title || '').toLowerCase();
    const productType = (item.product_type || '').toLowerCase();
    const vendor = (item.vendor || '').toLowerCase();

    if (productTitle.includes('wine') || productType.includes('wine') || vendor.includes('wine')) {
      hasWine = true;
    }
  });

  return hasWine;
}

/**
 * Calculate age from birth date
 */
function calculateAge(year, month, day) {
  const birthdate = new Date(year, month - 1, day);
  const today = new Date();
  let age = today.getFullYear() - birthdate.getFullYear();
  const monthDiff = today.getMonth() - birthdate.getMonth();
  const dayDiff = today.getDate() - birthdate.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  return age;
}

/**
 * Set button enabled/disabled state
 */
function setButtonState(button, enabled, title = '') {
  if (!button) return;

  button.disabled = !enabled;
  button.style.opacity = enabled ? '1' : '0.5';
  button.style.cursor = enabled ? 'pointer' : 'not-allowed';
  button.style.pointerEvents = enabled ? 'auto' : 'none';
  button.title = title;
}

/**
 * Update age verification UI
 */
function updateAgeVerificationUI(headerText, ageBox, message, color, borderColor) {
  if (headerText) {
    headerText.textContent = message;
    headerText.style.color = color;
  }
  if (ageBox) {
    ageBox.style.borderColor = borderColor;
  }
}

/**
 * Update cart attribute with age verification status
 */
function updateCartAttribute(status, formattedDate) {
  return fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: { 'Age Verification Status': `${status} (${formattedDate})` }
    })
  });
}

// ============================================================================
// MAIN FUNCTIONS
// ============================================================================

/**
 * Check age verification and update button state
 */
async function checkAgeAndToggleButtonDrawer() {
  try {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    const hasWineProduct = detectWineProducts(cart);
    const button = document.getElementById('multi-address-btn-drawer');
    const ageVerificationBox = document.getElementById('age-verification-drawer');

    // Show/hide age verification box based on wine products
    if (ageVerificationBox) {
      if (hasWineProduct) {
        ageVerificationBox.style.setProperty('display', 'block', 'important');

        // Force visibility after delay in case something overrides it
        setTimeout(() => {
          const box = document.getElementById('age-verification-drawer');
          if (box && box.style.display === 'none') {
            box.style.setProperty('display', 'block', 'important');
          }
        }, CONFIG.AGE_BOX_RETRY_DELAY);
      } else {
        ageVerificationBox.style.display = 'none';
      }
    }

    if (!button) return;

    // Handle button state based on wine products and age verification
    if (hasWineProduct) {
      const yearField = document.getElementById('birth-year-drawer');
      const monthField = document.getElementById('birth-month-drawer');
      const dayField = document.getElementById('birth-day-drawer');

      // Check if form fields are filled
      if (yearField?.value && monthField?.value && dayField?.value) {
        const age = calculateAge(yearField.value, monthField.value, dayField.value);
        const isLegalAge = age >= CONFIG.LEGAL_DRINKING_AGE;
        const title = isLegalAge ? '' : 'You must be 21 or older to use this option.';
        setButtonState(button, isLegalAge, title);
      } else {
        setButtonState(button, false, 'Please verify your age before proceeding.');
      }
    } else {
      setButtonState(button, true);
    }
  } catch (error) {
    console.error('Error checking age:', error);
  }
}

/**
 * Handle age verification form submission
 */
function handleAgeVerificationDrawer() {
  const year = document.getElementById('birth-year-drawer').value;
  const month = document.getElementById('birth-month-drawer').value;
  const day = document.getElementById('birth-day-drawer').value;
  const headerText = document.querySelector('#age-verification-drawer p');
  const ageBox = document.getElementById('age-verification-drawer');
  const button = document.getElementById('multi-address-btn-drawer');

  // Validate all fields are filled
  if (!year || !month || !day) {
    updateAgeVerificationUI(
      headerText,
      ageBox,
      'Please select your complete birthdate.',
      '#D2691E',
      '#D2691E'
    );
    return;
  }

  const age = calculateAge(year, month, day);
  const formattedDate = `${year}-${month}-${day}`;

  // Check if under legal drinking age
  if (age < CONFIG.LEGAL_DRINKING_AGE) {
    updateAgeVerificationUI(
      headerText,
      ageBox,
      'You must be at least 21 years old to purchase wine products.',
      '#c62828',
      '#c62828'
    );

    // Immediately disable button
    setButtonState(button, false, 'You must be 21 or older to use this option.');

    // Save to cart in background
    updateCartAttribute('Under 21', formattedDate)
      .catch(error => console.error('Error updating cart for under 21:', error));
    return;
  }

  // Age is 21 or older
  updateAgeVerificationUI(
    headerText,
    ageBox,
    'Age verified! You are 21 years or older.',
    '#4CAF50',
    '#4CAF50'
  );

  // Immediately enable button
  setButtonState(button, true);

  // Save to cart in background
  updateCartAttribute('Verified', formattedDate)
    .then(response => {
      if (!response.ok) {
        console.error('Failed to save age verification');
      }
    })
    .catch(error => console.error('Error submitting age verification:', error));
}

/**
 * Observe cart updates via fetch interception
 */
function observeCartUpdatesDrawer() {
  if (drawerFetchObserverSetup) return;
  drawerFetchObserverSetup = true;

  const originalFetch = window.fetch;

  window.fetch = async function (...args) {
    const [resource] = args;
    const response = await originalFetch.apply(this, args);

    try {
      const isCartUpdate =
        typeof resource === 'string' &&
        (resource.includes('/cart/update.js') ||
         resource.includes('/cart/change.js') ||
         resource.includes('/cart/add.js'));

      if (!isCartUpdate) return response;

      const cloned = response.clone();
      const text = await cloned.text();

      if (/^[\[{]/.test(text.trim())) {
        try {
          JSON.parse(text);
          setTimeout(() => {
            checkAgeAndToggleButtonDrawer();
            updateButtonStatusDrawer();
          }, CONFIG.CART_UPDATE_DELAY);
        } catch (err) {
          console.warn('Invalid JSON in cart update response');
        }
      }
    } catch (err) {
      console.error('Error in fetch observer:', err);
    }

    return response;
  };
}

/**
 * Update button visibility and eligibility
 */
async function updateButtonStatusDrawer() {
  const button = document.getElementById('multi-address-btn-drawer');

  return fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
      // Show/hide button based on cart count
      if (button) {
        button.style.display = cart.item_count === 0 ? 'none' : 'block';
      }

      if (cart.item_count === 0) return null;

      const hasWineProduct = detectWineProducts(cart);

      // Only enable button if no wine products (age verification handles wine case)
      if (button && !hasWineProduct) {
        setButtonState(button, true);
      }

      // Check for ineligible items
      const hasGiftCards = cart.items.some(item =>
        item.gift_card || /\b(?:gift[ _-]?card)\b/gi.test(item.product_type)
      );

      const hasSubscriptions = cart.items.some(item => item.selling_plan_allocation);

      const errorTarget = hasGiftCards && hasSubscriptions
        ? 'gift cards and subscriptions'
        : hasGiftCards ? 'gift cards'
        : hasSubscriptions ? 'subscriptions'
        : null;

      // Check for hide multiship tag
      let hideMultiship = false;
      cart.items.forEach(item => {
        const selector = `.cart-summary-item-container[data-title="${item.product_title.replace(/"/g, '&quot;')}"]`;
        const el = document.querySelector(selector);

        if (el) {
          const tags = el.dataset.tags ? el.dataset.tags.split(',') : [];
          if (tags.includes(CONFIG.HIDE_MULTISHIP_TAG)) {
            hideMultiship = true;
          }
        }
      });

      if (errorTarget || hideMultiship) {
        if (button) {
          setButtonState(button, false, `We're sorry — ${errorTarget || 'this item'} isn't eligible for this feature.`);
        }
        return null;
      }

      return cart.token;
    })
    .catch(error => {
      console.error('Error fetching cart:', error);
      return null;
    });
}

// ============================================================================
// EVENT LISTENERS & INITIALIZATION
// ============================================================================

/**
 * Initialize multi-address button and age verification
 */
function initMultiAddressButtonDrawer() {
  // Setup multi-address button click handler
  if (!drawerButtonHandlerSetup) {
    drawerButtonHandlerSetup = true;

    document.addEventListener('click', async function (e) {
      const multiAddressBtn = e.target.closest('#multi-address-btn-drawer');
      if (!multiAddressBtn) return;

      // Block disabled clicks
      if (multiAddressBtn.disabled) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }

      e.preventDefault();
      e.stopPropagation();

      // Prevent double-clicks
      if (multiAddressBtn.classList.contains('processing')) return;

      // Add loading state
      const originalText = multiAddressBtn.innerHTML;
      multiAddressBtn.classList.add('processing');
      multiAddressBtn.disabled = true;
      multiAddressBtn.innerHTML = '<span style="display:inline-flex;align-items:center;gap:8px;"><svg style="animation: spin 1s linear infinite;" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" opacity="0.25"/><path d="M12 2 A10 10 0 0 1 22 12" opacity="0.75"/></svg> Processing...</span>';

      const eligibleCartTokenResponse = await updateButtonStatusDrawer();

      if (eligibleCartTokenResponse) {
        window.location.href = `https://${Shopify.shop}/apps/zest/start-multi-ship/${eligibleCartTokenResponse}`;
      } else {
        // Reset button if not redirecting
        multiAddressBtn.classList.remove('processing');
        multiAddressBtn.disabled = false;
        multiAddressBtn.innerHTML = originalText;
      }
    }, true);
  }

  // Setup age verification listeners
  if (!drawerAgeVerificationSetup) {
    drawerAgeVerificationSetup = true;

    // Handle birthdate field changes
    document.addEventListener('change', (e) => {
      if (e.target.id === 'birth-year-drawer' ||
          e.target.id === 'birth-month-drawer' ||
          e.target.id === 'birth-day-drawer') {

        const yearSelect = document.getElementById('birth-year-drawer');
        const monthSelect = document.getElementById('birth-month-drawer');
        const daySelect = document.getElementById('birth-day-drawer');

        // Auto-submit when all three fields are filled
        if (yearSelect?.value && monthSelect?.value && daySelect?.value) {
          handleAgeVerificationDrawer();
        }
      }
    });

    // Monitor cart item count changes
    let lastItemCount = 0;
    setInterval(() => {
      const currentItemCount = document.querySelectorAll('.cart-summary-item-container').length;

      if (currentItemCount > 0 && currentItemCount !== lastItemCount) {
        checkAgeAndToggleButtonDrawer();
        updateButtonStatusDrawer();
        lastItemCount = currentItemCount;
      } else if (currentItemCount === 0 && lastItemCount > 0) {
        lastItemCount = 0;
      }
    }, CONFIG.CART_UPDATE_DELAY);

    // Trigger on cart-related clicks
    document.addEventListener('click', (e) => {
      if (e.target.closest('.toggle-cart-summary') ||
          e.target.id === 'cart-link' ||
          e.target.closest('[href*="cart"]')) {
        setTimeout(() => {
          checkAgeAndToggleButtonDrawer();
          updateButtonStatusDrawer();
        }, CONFIG.CART_UPDATE_DELAY);
      }
    }, true);
  }

  // Initial checks
  checkAgeAndToggleButtonDrawer();
  updateButtonStatusDrawer();
  observeCartUpdatesDrawer();
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMultiAddressButtonDrawer);
} else {
  initMultiAddressButtonDrawer();
}
